import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { BannerService } from '../../core/services/banner.service';
import { Banner } from '../../models/banner.interface';

@Component({
  selector: 'app-banner-list',
  standalone: true,
  imports: [CommonModule, RouterModule, SidebarComponent, NavbarComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar pageTitle="Banners" pageSubtitle="Manage homepage hero slides"></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">

        <!-- Header -->
        <div class="page-top-bar">
          <div>
            <h2 class="font-serif title-gold" style="font-size: 20px; font-weight: 700;">
              Hero Banners
              <span class="count-badge">{{ banners.length }}</span>
            </h2>
            <p class="sub-text" style="margin-top: 3px;">Manage hero slides for Home page and Collections page</p>
          </div>
          <div style="display:flex;align-items:center;gap:12px">
            <!-- Placement Filter Tabs -->
            <div class="placement-filter-tabs">
              <button
                class="tab-btn"
                [class.active]="selectedPlacement === 'all'"
                (click)="selectedPlacement = 'all'"
              >
                All ({{ banners.length }})
              </button>
              <button
                class="tab-btn"
                [class.active]="selectedPlacement === 'home'"
                (click)="selectedPlacement = 'home'"
              >
                🏠 Home ({{ getPlacementCount('home') }})
              </button>
              <button
                class="tab-btn"
                [class.active]="selectedPlacement === 'collection'"
                (click)="selectedPlacement = 'collection'"
              >
                📂 Collection ({{ getPlacementCount('collection') }})
              </button>
              <button
                class="tab-btn"
                [class.active]="selectedPlacement === 'product'"
                (click)="selectedPlacement = 'product'"
              >
                🛍️ Product ({{ getPlacementCount('product') }})
              </button>
            </div>

            <a routerLink="/banners/new" class="btn-gold">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
              Add Banner
            </a>
          </div>
        </div>

        <!-- Banners Grid -->
        <div class="banners-grid" *ngIf="filteredBanners.length > 0">
          <div *ngFor="let banner of filteredBanners" class="banner-card glass-card glass-card-hover">

            <!-- Image Preview -->
            <div class="banner-preview">
              <img [src]="banner.imageUrl" [alt]="banner.heading" class="banner-img" />
              <div class="banner-gradient"></div>
              <div class="banner-overlay-top">
                <span class="badge badge-gold">{{ banner.badge || 'HERO DROP' }}</span>
                <span class="badge" [style.background]="getPlacementBg(banner.placement)" style="font-weight:700;font-size:10px;text-transform:uppercase">
                  {{ getPlacementLabel(banner.placement) }}
                </span>
                <span class="badge" [style.background]="banner.mobileImageUrl ? 'rgba(16, 185, 129, 0.35)' : 'rgba(255, 255, 255, 0.15)'" style="font-size:10px;font-weight:600">
                  {{ banner.mobileImageUrl ? '📱 Mobile Ready' : '🖥️ Desktop' }}
                </span>
              </div>
              <div class="banner-overlay-bottom">
                <span
                  class="active-toggle"
                  [class.is-active]="banner.active"
                  title="Active status"
                >
                  <span class="active-dot"></span>
                  {{ banner.active ? 'Live' : 'Hidden' }}
                </span>
              </div>
            </div>

            <!-- Body -->
            <div class="banner-body">
              <h3 class="banner-heading font-serif">{{ banner.heading }}</h3>
              <p class="banner-sub">{{ banner.sub || 'No subtitle provided.' }}</p>
              <div class="banner-cta-row">
                <span class="cta-badge">CTA: {{ banner.cta || 'Explore' }}</span>
                <span class="placement-tag">
                  📍 {{ getPlacementFullLabel(banner.placement) }}
                </span>
              </div>
            </div>

            <!-- Actions Footer -->
            <div class="banner-footer">
              <a [routerLink]="['/banners/edit', banner._id]" class="btn-secondary" style="font-size: 12px; padding: 7px 14px;">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Edit
              </a>
              <button (click)="deleteBanner(banner)" class="btn-danger" style="font-size: 12px; padding: 7px 14px;">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="banners.length === 0" class="glass-card">
          <div class="empty-state">
            <div class="empty-state-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"/>
                <line x1="2" y1="10" x2="22" y2="10"/>
              </svg>
            </div>
            <h4>No hero banners yet</h4>
            <p>Add your first hero banner to start populating the homepage slider</p>
          </div>
        </div>

      </main>
    </div>
  `,
  styles: [`
    .page-top-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 16px;
    }

    .count-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: var(--accent-gold-glow);
      color: var(--accent-gold);
      border: 1px solid var(--border-highlight);
      font-size: 11px;
      font-weight: 800;
      padding: 2px 8px;
      border-radius: var(--radius-full);
      margin-left: 8px;
      font-family: var(--font-sans);
    }

    .banners-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 20px;
    }

    .banner-card {
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    /* Preview */
    .banner-preview {
      height: 175px;
      position: relative;
      background: #0F1624;
      overflow: hidden;
    }

    .banner-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform var(--transition-slow);
    }

    .banner-card:hover .banner-img {
      transform: scale(1.04);
    }

    .banner-gradient {
      position: absolute;
      inset: 0;
      background: linear-gradient(0deg, rgba(8,12,20,0.6) 0%, transparent 50%);
      pointer-events: none;
    }

    .banner-overlay-top {
      position: absolute;
      top: 12px;
      left: 12px;
      z-index: 2;
    }

    .banner-overlay-bottom {
      position: absolute;
      bottom: 12px;
      right: 12px;
      z-index: 2;
    }

    .active-toggle {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 10.5px;
      font-weight: 700;
      padding: 4px 10px;
      border-radius: var(--radius-full);
      background: var(--danger-bg);
      color: var(--danger);
      border: 1px solid rgba(248, 113, 113, 0.25);
    }

    .active-toggle.is-active {
      background: var(--success-bg);
      color: var(--success);
      border-color: rgba(52, 211, 153, 0.25);
    }

    .active-dot {
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: currentColor;
    }

    .active-toggle.is-active .active-dot {
      animation: pulse 2s infinite;
    }

    /* Body */
    .banner-body {
      padding: 18px 18px 12px;
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .banner-heading {
      font-size: 16px;
      font-weight: 700;
      color: var(--text-primary);
      line-height: 1.3;
    }

    .banner-sub {
      font-size: 12.5px;
      color: var(--text-secondary);
      line-height: 1.5;
      flex: 1;
    }

    .banner-cta-row {
      margin-top: 6px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 6px;
    }

    .cta-badge {
      font-size: 11px;
      color: var(--accent-gold);
      font-weight: 600;
      background: var(--accent-gold-glow);
      border: 1px solid var(--border-highlight);
      padding: 3px 10px;
      border-radius: var(--radius-full);
    }

    .placement-tag {
      font-size: 11px;
      color: var(--text-secondary);
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.08);
      padding: 3px 8px;
      border-radius: var(--radius-sm);
      font-weight: 600;
    }

    /* Placement Filter Tabs */
    .placement-filter-tabs {
      display: flex;
      align-items: center;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      padding: 3px;
      gap: 2px;
    }
    .tab-btn {
      background: transparent;
      border: none;
      color: var(--text-secondary);
      font-size: 12px;
      font-weight: 600;
      padding: 6px 12px;
      border-radius: var(--radius-sm);
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .tab-btn:hover {
      color: var(--text-primary);
      background: rgba(255, 255, 255, 0.05);
    }
    .tab-btn.active {
      background: rgba(201, 169, 110, 0.18);
      color: var(--accent-gold);
      border: 1px solid var(--border-highlight);
      font-weight: 700;
    }

    /* Footer */
    .banner-footer {
      padding: 12px 18px;
      border-top: 1px solid var(--border-subtle);
      display: flex;
      align-items: center;
      gap: 10px;
      justify-content: flex-end;
      background: rgba(0,0,0,0.15);
    }

    @keyframes pulse {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 1; }
    }
  `]
})
export class BannerListComponent implements OnInit {
  banners: Banner[] = [];
  selectedPlacement: 'all' | 'home' | 'collection' | 'product' | 'shop' = 'all';

  constructor(private bannerService: BannerService) {}

  ngOnInit(): void {
    this.loadBanners();
  }

  loadBanners(): void {
    this.bannerService.getBanners().subscribe({
      next: (res) => (this.banners = res),
      error: (err) => console.error(err)
    });
  }

  get filteredBanners(): Banner[] {
    if (this.selectedPlacement === 'all') {
      return this.banners;
    }
    return this.banners.filter((b) => {
      const placement = b.placement || 'home';
      return placement === this.selectedPlacement || placement === 'both';
    });
  }

  getPlacementCount(placement: 'home' | 'collection' | 'product'): number {
    return this.banners.filter((b) => {
      const p = b.placement || 'home';
      return p === placement || p === 'both';
    }).length;
  }

  getPlacementLabel(placement?: string): string {
    const p = placement || 'home';
    if (p === 'collection') return '📂 COLLECTION';
    if (p === 'product') return '🛍️ PRODUCT';
    if (p === 'shop') return '🛒 SHOP';
    if (p === 'both') return '🌐 ALL';
    return '🏠 HOME';
  }

  getPlacementFullLabel(placement?: string): string {
    const p = placement || 'home';
    if (p === 'collection') return 'Collections Page';
    if (p === 'product') return 'Product Page Slider';
    if (p === 'shop') return 'Shop Page Slider';
    if (p === 'both') return 'All Pages';
    return 'Home Page';
  }

  getPlacementBg(placement?: string): string {
    const p = placement || 'home';
    if (p === 'collection') return 'rgba(124, 58, 237, 0.35)';
    if (p === 'product') return 'rgba(234, 179, 8, 0.35)';
    if (p === 'both') return 'rgba(59, 130, 246, 0.35)';
    return 'rgba(34, 197, 94, 0.3)';
  }

  deleteBanner(banner: Banner): void {
    if (!banner._id) return;
    if (confirm(`Are you sure you want to delete banner "${banner.heading}"?`)) {
      this.bannerService.deleteBanner(banner._id).subscribe({
        next: () => {
          this.banners = this.banners.filter((b) => b._id !== banner._id);
        },
        error: (err) => alert('Failed to delete banner: ' + err.message)
      });
    }
  }
}

